import { ReactNode, useState, useEffect } from "react";
import { useRole } from "../hooks/useRole";
import { UnauthorizedWelcome } from "./UnauthorizedWelcome";

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isLoggedIn, loading, isDemo } = useRole();
  const [apiUnauthorizedReason, setApiUnauthorizedReason] = useState<string | null>(null);

  useEffect(() => {
    const handleUnauthorized = (e: Event) => {
      const customEvent = e as CustomEvent<{ status: number; url?: string }>;
      const urlText = customEvent.detail?.url ? ` (${customEvent.detail.url})` : "";
      setApiUnauthorizedReason(`Session expired or unauthorized request${urlText}`);
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a09] text-stone-400 font-mono text-xs gap-3">
        <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent animate-spin" />
        <div className="tracking-widest uppercase text-stone-500">
          Decrypting session gateway...
        </div>
      </div>
    );
  }

  // Not authenticated and not in demo mode -> show weird welcome card
  if (!isLoggedIn && !isDemo) {
    return <UnauthorizedWelcome reason={apiUnauthorizedReason || undefined} />;
  }

  // API returned 401 while inside app -> show weird welcome card with diagnostic reason
  if (apiUnauthorizedReason && !isDemo) {
    return <UnauthorizedWelcome reason={apiUnauthorizedReason} />;
  }

  return <>{children}</>;
}
